import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Users, FileImage, Gift, TrendingUp, ArrowRight, AlertTriangle, RefreshCw, PartyPopper,
  ChevronRight, ChevronDown,
} from 'lucide-react';
import type { NeedsAttentionResponse } from '@crewcast/shared';
import { authConfig } from '../../config/auth';
import { useAuth } from '../../contexts/AuthContext';

const CARDS = [
  {
    to: '/backoffice/accounts',
    icon: <Building2 size={24} />,
    iconBg: '#dcfce7',
    iconColor: '#16a34a',
    title: 'Accounts & Users',
    desc: 'Company list with admin/employee counts, invite funnel stats, and last-login tracking.',
  },
  {
    to: '/backoffice/engagement',
    icon: <Users size={24} />,
    iconBg: '#dbeafe',
    iconColor: '#1d4ed8',
    title: 'Engagement',
    desc: 'Monthly active users and Moments activity, filterable by date range and account.',
  },
  {
    to: '/backoffice/content',
    icon: <FileImage size={24} />,
    iconBg: '#fef9c3',
    iconColor: '#a16207',
    title: 'Content Pipeline',
    desc: 'Submission volume and moderation stats — approved, rejected, and pending content.',
  },
  {
    to: '/backoffice/rewards',
    icon: <Gift size={24} />,
    iconBg: '#fce7f3',
    iconColor: '#be185d',
    title: 'Rewards & Fulfillment',
    desc: 'Claimed, fulfilled, and pending reward requests with points breakdown per account.',
  },
  {
    to: '/backoffice/kudos',
    icon: <PartyPopper size={24} />,
    iconBg: '#ede9fe',
    iconColor: '#7c3aed',
    title: 'Kudos',
    desc: 'Audit trail of every kudos sent: who sent it, to whom, the message, and any bonus points.',
  },
  {
    to: '/backoffice/trends',
    icon: <TrendingUp size={24} />,
    iconBg: '#ede9fe',
    iconColor: '#7c3aed',
    title: 'Suggested Trends',
    desc: 'Create and manage global or company-specific content trends to inspire employee submissions.',
  },
];

/**
 * Accounts tripping a red-flag rule (computed live by the API, so a flag
 * clears as soon as the account recovers; canceled accounts are left out).
 * One compact row per account; click it to see which rules fired.
 */
function NeedsAttentionPanel() {
  const { token } = useAuth();
  const [data, setData] = useState<NeedsAttentionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (companyId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${authConfig.apiUrl}/api/backoffice/needs-attention`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load accounts needing attention');
      setData(await res.json());
      setError('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="bo-table-card bo-attention">
      <div className="bo-table-header">
        <h2 className="bo-table-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={18} color="#d97706" />
          Needs attention
          {data && (
            <span className="bo-table-count">
              {data.accounts.length} of {data.accountsChecked} accounts
            </span>
          )}
        </h2>
        <button type="button" className="bo-btn bo-btn-secondary bo-btn-sm" onClick={fetchData} disabled={loading}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
      {error && <p className="bo-attention-empty">{error}</p>}
      {!error && loading && !data && <p className="bo-attention-empty">Checking accounts…</p>}
      {!error && data && data.accounts.length === 0 && (
        <p className="bo-attention-empty">Every account looks healthy.</p>
      )}
      {!error && data && data.accounts.length > 0 && (
        <ul className="bo-attention-list">
          {data.accounts.map((account) => {
            const open = expanded.has(account.companyId);
            return (
              <li key={account.companyId} className="bo-attention-item">
                <button
                  type="button"
                  className="bo-attention-row"
                  onClick={() => toggle(account.companyId)}
                  aria-expanded={open}
                  aria-controls={`attention-${account.companyId}`}
                >
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className="bo-attention-name">{account.displayName}</span>
                  <span className="bo-badge bo-badge-red">
                    {account.flags.length} issue{account.flags.length === 1 ? '' : 's'}
                  </span>
                </button>
                {open && (
                  <ul id={`attention-${account.companyId}`} className="bo-attention-flags">
                    {account.flags.map((flag) => (
                      <li key={flag.rule} className="bo-attention-flag">
                        <span className="bo-attention-label">{flag.label}</span>
                        <span className="bo-attention-detail">{flag.detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function BackOfficeDashboardPage() {
  return (
    <>
      <div className="bo-page-header">
        <h1 className="bo-page-title">Back Office</h1>
        <p className="bo-page-subtitle">
          Internal dashboard — account health, engagement, content, and rewards across all companies.
        </p>
      </div>

      <NeedsAttentionPanel />

      <div className="bo-nav-card-grid">
        {CARDS.map((card) => (
          <Link key={card.to} to={card.to} className="bo-nav-card">
            <div
              className="bo-nav-card-icon"
              style={{ background: card.iconBg, color: card.iconColor }}
            >
              {card.icon}
            </div>
            <h2 className="bo-nav-card-title">{card.title}</h2>
            <p className="bo-nav-card-desc">{card.desc}</p>
            <span className="bo-nav-card-arrow">
              View section <ArrowRight size={14} />
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}
